import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Loader2, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEO, { breadcrumbSchema } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchPosts();
  }, [page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/posts?page=${page}&limit=9`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
        setTotalPages(data.pages);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <SEO
        title="Blog – Werbung & Aussenwerbung im Zürcher Oberland"
        description="Tipps, Ratgeber und Insights rund um mobile Werbeflächen und Aussenwerbung im Zürcher Oberland. Erfahren Sie, wie Sie Ihre Werbung effektiv platzieren."
        canonicalPath="/blog"
        structuredData={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' }
        ])}
      />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[hsl(var(--primary)/0.05)] to-transparent pt-12 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-4">
              Blog & Ratgeber
            </h1>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              Tipps und Insights rund um mobile Werbeflächen und Aussenwerbung im Zürcher Oberland
            </p>
          </motion.div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto text-[hsl(var(--muted-foreground))] opacity-40 mb-4" />
            <h2 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">Noch keine Beiträge</h2>
            <p className="text-[hsl(var(--muted-foreground))]">Bald finden Sie hier spannende Artikel rund um Aussenwerbung.</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-posts-grid">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link to={`/blog/${post.slug}`} className="block group">
                    <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow duration-300 border-[hsl(var(--border))]">
                      {post.featured_image && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={post.featured_image.startsWith('/') ? `${API_URL}${post.featured_image}` : post.featured_image}
                            alt={post.image_alt || post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <CardContent className={post.featured_image ? "pt-4" : "pt-6"}>
                        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] mb-3">
                          <Calendar className="w-3.5 h-3.5" />
                          {post.published_at ? new Date(post.published_at).toLocaleDateString('de-CH', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                          {post.ai_generated && (
                            <Badge variant="outline" className="text-xs ml-auto">KI</Badge>
                          )}
                        </div>
                        <h2 className="font-semibold text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2 mb-2">
                          {post.title}
                        </h2>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center gap-1 mt-4 text-sm font-medium text-[hsl(var(--primary))]">
                          Weiterlesen <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-10">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Zurück
                </Button>
                <span className="flex items-center px-4 text-sm text-[hsl(var(--muted-foreground))]">
                  Seite {page} von {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Weiter
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
